import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Clock, Mail, CheckCircle, ArrowLeft, Shield } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const PendingApproval = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userEmail = location.state?.email || 'your email';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-2xl">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-6 pt-12">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-orange-600" />
              </div>
              <CardTitle className="text-3xl font-bold text-slate-900 mb-3">
                Thank You for Signing Up!
              </CardTitle>
              <CardDescription className="text-lg text-slate-600">
                Your account is currently under review
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pb-12 px-8">
              <div className="space-y-6">
                {/* Success Message */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Mail className="w-6 h-6 text-orange-600 mt-1" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">
                        Registration Successful
                      </h3>
                      <p className="text-slate-700 leading-relaxed">
                        We've received your registration request for <strong>{userEmail}</strong>. 
                        Our team is currently reviewing your details to ensure the security and quality of our platform.
                      </p>
                    </div>
                  </div>
                </div>

                {/* What Happens Next */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-navy-600" />
                    What Happens Next?
                  </h3>
                  
                  <div className="space-y-4 ml-7">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-navy-600 font-semibold text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Review Process</p>
                        <p className="text-sm text-slate-600">
                          Our team will review your registration details within 24-48 hours.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-navy-600 font-semibold text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Email Notification</p>
                        <p className="text-sm text-slate-600">
                          Once approved, you'll receive an email confirmation with instructions to access your account.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-navy-600 font-semibold text-sm">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Access AI-Powered Compliance</p>
                        <p className="text-sm text-slate-600">
                          Start your journey to fast-track SOC 2, GDPR, ISO 27001, and PCI DSS certifications.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* What You'll Get */}
                <div className="bg-navy-50 border border-navy-200 rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Shield className="w-6 h-6 text-navy-600 mt-1" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3">
                        What You'll Get Access To:
                      </h3>
                      <ul className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-center">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                          AI-powered compliance and certification platform
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                          Automated evidence collection from your systems
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                          Expert guidance on certification readiness
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                          Fast-track your path to enterprise sales
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Need Help */}
                <div className="text-center pt-4 border-t border-slate-200">
                  <p className="text-slate-600 mb-4">
                    Need assistance or have questions?
                  </p>
                  <a 
                    href="mailto:support@teamnova.ai"
                    className="inline-flex items-center text-navy-600 hover:text-navy-700 font-medium"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    support@teamnova.ai
                  </a>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-6">
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="flex-1 border-slate-200 hover:bg-slate-50"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                  <Button
                    onClick={() => navigate('/login')}
                    className="flex-1 bg-navy-600 hover:bg-navy-700 text-white"
                  >
                    Try to Sign In
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PendingApproval;
